import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Popup from "../../../components/Popup";
import {
  Badge,
  EmptyState,
  Page,
  PageHeader,
  Pagination,
  SearchInput,
  Segmented,
  TableSkeleton,
  usePagination,
} from "../../../components/ui";
import {
  addItemToSlider,
  deleteSliderItem,
  getMovieVideoSlides,
  getShowPodcastSlides,
  getSliderBySliderType,
} from "../../../services/sliders";
import { getChannelList } from "../../../services/channels";
import { matches, newestFirst } from "../../../utils/list";
import "./sliders.css";

/** Sliders of the apps and the kinds of content each one shows. */
export const SLIDERS = [
  { type: "home", label: "Home", sources: ["movie", "tv_show", "podcast"] },
  { type: "movie", label: "Videos", sources: ["movie"] },
  { type: "tv_show", label: "Series", sources: ["tv_show"] },
  { type: "podcast", label: "Podcasts", sources: ["podcast"], hidden: true },
  { type: "channel", label: "Live channels", sources: ["channel"], hidden: true },
];

/** Contenus masqués pour l'instant (podcasts, chaînes en direct) : plus proposés dans les carrousels. */
const HIDDEN_SOURCES = ["podcast", "channel"];
const choosableSources = (slider) => slider.sources.filter((item) => !HIDDEN_SOURCES.includes(item));

const SOURCE_LABELS = {
  movie: "Videos",
  tv_show: "Series",
  podcast: "Podcasts",
  channel: "Live channels",
};

/** Content (movie, show or channel) of a slide. */
export const slideContent = (slide) =>
  ({
    movie: slide.general_content,
    tv_show: slide.show,
    podcast: slide.show,
    channel: slide.channel,
  }[slide.category]);

/** Title of a movie, show or channel. */
export const contentTitle = (content, source) =>
  (source === "movie" ? content?.media?.title : content?.title) || "N/A";

/** Slide of `slides` showing the content `item` of kind `source`, if any. */
export const findSlide = (slides, item, source) =>
  slides.find((slide) => {
    const content =
      source === "movie"
        ? slide.general_content
        : source === "channel"
        ? slide.channel
        : slide.show;
    return content?._id === item._id;
  });

/** Number of available contents shown per page. */
const PAGE_SIZE = 12;

/** Contents that can be added to the sliders, per kind, newest first (shorts excluded). */
async function loadContents() {
  const [movies, shows, channels] = await Promise.all([
    getMovieVideoSlides(),
    getShowPodcastSlides(),
    getChannelList(),
  ]);
  const allShows = shows?.allTvShows || [];
  // Newest contents first (filter returns new arrays, sorted in place); the
  // current slides keep the order of the API.
  return {
    movie: newestFirst(
      (movies?.allMovies || []).filter(
        (item) => item.category === "movie" && item.isShort !== true
      )
    ),
    tv_show: newestFirst(allShows.filter((item) => item.category === "tvshow")),
    podcast: newestFirst(
      allShows.filter((item) => item.category === "podcast")
    ),
    channel: newestFirst(
      (channels?.allChannels || []).filter((item) => item.status === true)
    ),
  };
}

const Sliders = () => {
  const { t, i18n } = useTranslation();
  const [sliderType, setSliderType] = useState("home");
  const slider = SLIDERS.find((item) => item.type === sliderType);
  const [source, setSource] = useState("movie");
  const [search, setSearch] = useState("");

  const [slides, setSlides] = useState({});
  const [contents, setContents] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [toRemove, setToRemove] = useState(null);

  const refresh = (type) =>
    getSliderBySliderType(type).then((result) =>
      setSlides((current) => ({ ...current, [type]: result || [] }))
    );

  useEffect(() => {
    loadContents().then(setContents);
  }, []);
  // The slides depend on the language of the admin.
  useEffect(() => {
    SLIDERS.forEach(({ type }) => refresh(type));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const current = slides[sliderType];
  const sources = choosableSources(slider);
  const activeSource = sources.includes(source) ? source : sources[0];
  const candidates = useMemo(
    () =>
      (contents?.[activeSource] || []).filter((item) =>
        matches(contentTitle(item, activeSource), search)
      ),
    [contents, activeSource, search]
  );
  const pagination = usePagination(candidates, PAGE_SIZE, [
    sliderType,
    activeSource,
    search,
  ]);

  const chooseSlider = (type) => {
    setSliderType(type);
    setSource(SLIDERS.find((item) => item.type === type).sources[0]);
    setSearch("");
  };

  const add = async (item) => {
    setBusyId(item._id);
    const added = await addItemToSlider({
      general_content_id: item._id,
      slider_type: sliderType,
      category: activeSource,
    });
    await refresh(sliderType);
    setBusyId(null);
    if (added) toast.success(t("Slide added"));
    else toast.error(t("Adding Slide Failed!"));
  };

  const remove = async (slide) => {
    setBusyId(slide._id);
    try {
      await deleteSliderItem(slide._id);
      toast.success(t("Slider Item Deleted Successfully!"));
    } catch (error) {
      toast.error(t("Removing Slide Failed!"));
    }
    await refresh(sliderType);
    setBusyId(null);
  };

  return (
    <Page>
      <PageHeader
        title={t("Sliders")}
        subtitle={t("Banners shown at the top of each page of the apps")}
      />

      <div className="sliders-tabs">
        <Segmented
          label={t("Sliders")}
          value={sliderType}
          onChange={chooseSlider}
          options={SLIDERS.filter((item) => !item.hidden).map((item) => ({
            value: item.type,
            label: `${t(item.label)} (${slides[item.type]?.length ?? "…"})`,
          }))}
        />
      </div>

      <section className="ui-card sliders-card">
        <h2 className="sliders-title">{t("Current slides")}</h2>
        {!current ? (
          <TableSkeleton rows={2} />
        ) : current.length === 0 ? (
          <EmptyState icon="ri-slideshow-line">
            {t("No Slides To Show")}
          </EmptyState>
        ) : (
          <ul className="sliders-grid">
            {current.map((slide, index) => {
              const content = slideContent(slide);
              return (
                <li key={slide._id} className="slider-tile">
                  <span className="slider-order">{index + 1}</span>
                  <img
                    src={content?.thumbnail?.static_thumbnail_url || ""}
                    alt=""
                  />
                  <div className="slider-tile-body">
                    <strong title={contentTitle(content, slide.category)}>
                      {contentTitle(content, slide.category)}
                    </strong>
                    {slider.sources.length > 1 && (
                      <Badge color="blue">
                        {t(SOURCE_LABELS[slide.category])}
                      </Badge>
                    )}
                  </div>
                  <button
                    type="button"
                    className="ui-icon-btn slider-remove"
                    data-tone="danger"
                    title={t("Remove Slide")}
                    aria-label={t("Remove Slide")}
                    disabled={busyId === slide._id}
                    onClick={() => setToRemove(slide)}
                  >
                    <i className="ri-close-line" aria-hidden="true"></i>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="ui-card">
        <div className="ui-toolbar">
          <h2 className="sliders-title">{t("Available contents")}</h2>
          <div className="sliders-filters">
            {sources.length > 1 && (
              <Segmented
                label={t("type")}
                value={activeSource}
                onChange={setSource}
                options={sources.map((item) => ({
                  value: item,
                  label: t(SOURCE_LABELS[item]),
                }))}
              />
            )}
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t("Search")}
            />
          </div>
        </div>
        {!contents || !current ? (
          <TableSkeleton />
        ) : candidates.length === 0 ? (
          <EmptyState icon="ri-search-line">{t("No Data Found")}</EmptyState>
        ) : (
          <>
            <ul className="sliders-candidates">
              {pagination.pageItems.map((item) => {
                const slide = findSlide(current, item, activeSource);
                const busy =
                  busyId === item._id || (slide && busyId === slide._id);
                return (
                  <li key={item._id} data-added={slide ? "" : undefined}>
                    <span className="slider-candidate-thumb">
                      <img
                        src={item.thumbnail?.static_thumbnail_url || ""}
                        alt=""
                        loading="lazy"
                      />
                      {slide && (
                        <span className="slider-candidate-added">
                          <i className="ri-check-line" aria-hidden="true"></i>
                          {t("In the slider")}
                        </span>
                      )}
                    </span>
                    <span
                      className="slider-candidate-title"
                      title={contentTitle(item, activeSource)}
                    >
                      {contentTitle(item, activeSource)}
                    </span>
                    <button
                      type="button"
                      className="ui-btn ui-btn-secondary"
                      disabled={busy}
                      onClick={() => (slide ? setToRemove(slide) : add(item))}
                    >
                      {busy ? (
                        <span className="ad-spinner" aria-hidden="true" />
                      ) : (
                        <i
                          className={slide ? "ri-close-line" : "ri-add-line"}
                          aria-hidden="true"
                        ></i>
                      )}
                      {slide ? t("Remove Slide") : t("Add Slide")}
                    </button>
                  </li>
                );
              })}
            </ul>
            <Pagination {...pagination} onChange={pagination.setPage} />
          </>
        )}
      </section>

      {toRemove && (
        <Popup
          open={true}
          setOpen={() => setToRemove(null)}
          deleteHandle={() => remove(toRemove)}
        >
          <div className="model-wrapper">
            {t("Are you sure, you want to delete this slider item?")}
          </div>
        </Popup>
      )}
    </Page>
  );
};

export default Sliders;
